import isNil from 'lodash/isNil';
import { useTranslation } from 'react-i18next';
import SimpleSelect from '~/components/inputs/Select/SimpleSelect';
import { DocumentDirection, DocumentPaymentStatus } from '~/types/document';

type NameType<T> = T extends string ? string : T extends {} ? keyof T : string;

interface DocumentPaymentStatusFilterProps<Filters extends string | {}> {
  className?: string;
  label?: string;
  placeholder?: string;
  name: NameType<Filters>;
  onChange: (name: NameType<Filters>, value: DocumentPaymentStatus | null) => void;
  value: DocumentPaymentStatus | null;
  direction?: DocumentDirection;
  clearable?: boolean;
  hideLabel?: boolean;
}

const DocumentPaymentStatusFilter = <Filters extends string | {}>({
  className,
  label,
  placeholder,
  name,
  onChange,
  direction,
  value,
  clearable,
  hideLabel,
}: DocumentPaymentStatusFilterProps<Filters>) => {
  const { t } = useTranslation('inputs');

  const resolvedLabel = label ?? t('document_payment_status_filters.label');

  return (
    <SimpleSelect
      className={className}
      label={resolvedLabel}
      placeholder={placeholder ?? resolvedLabel}
      name={name}
      value={value}
      clearable={clearable}
      hideLabel={hideLabel}
      onChange={(newValue) => onChange(name, newValue as DocumentPaymentStatus | null)}
      options={[
        {
          label: t(
            isNil(direction)
              ? 'document_payment_status_filters.generic.to_pay'
              : direction === DocumentDirection.issued
                ? 'document_payment_status_filters.inflow.to_pay'
                : 'document_payment_status_filters.outflow.to_pay',
          ),
          value: DocumentPaymentStatus.ToPay,
        },
        {
          label: t(
            isNil(direction)
              ? 'document_payment_status_filters.generic.paid'
              : direction === DocumentDirection.issued
                ? 'document_payment_status_filters.inflow.paid'
                : 'document_payment_status_filters.outflow.paid',
          ),
          value: DocumentPaymentStatus.Paid,
        },
        {
          label: t(
            isNil(direction)
              ? 'document_payment_status_filters.generic.partially_paid'
              : direction === DocumentDirection.issued
                ? 'document_payment_status_filters.inflow.partially_paid'
                : 'document_payment_status_filters.outflow.partially_paid',
          ),
          value: DocumentPaymentStatus.PartiallyPaid,
        },
      ]}
    />
  );
};

export default DocumentPaymentStatusFilter;
